'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { selectCartItems, useCartStore } from '@/store/use-cart.store';

import { useRemoveBasketAllItems } from '@/common/apis/cart/basket-remove-all';
import { useRemoveBasketItem } from '@/common/apis/cart/basket-remove-item';
import { useCreateBasket } from '@/common/apis/cart/create-basket';

import { useSession } from './use-session.hook';

export const useBasket = (vID: number) => {
  const cartItems = useCartStore(selectCartItems);
  const [vendorId, setVendorId] = useState(vID);
  const { mutate: createBastekMutate } = useCreateBasket();
  const { mutate: removeItemMutate } = useRemoveBasketItem();
  const { mutate: removeAllMutate } = useRemoveBasketAllItems();
  const { status } = useSession();
  const previousOrders = useRef(
    useMemo(
      () =>
        cartItems.find((cart) => cart.vendor.id === vendorId)?.products || [],
      []
    )
  );
  const firstLoad = useRef(true);

  const setVendor = useCallback((id: number) => {
    setVendorId(id);
  }, []);

  const createBasket = () => {
    const vendor = cartItems.find((cart) => cart.vendor.id === vendorId);
    if (vendor && status === 'authenticated') {
      const command = vendor.products.map((item) => {
        return {
          count: item.quantity,
          productVariantId: item.productVariantId,
        };
      });
      createBastekMutate({ vendorId, command });
    }
  };

  const removeBasket = (id: number) => {
    if (status === 'authenticated') removeAllMutate({ vendorId: id });
  };

  const removeBasketItem = (productVariantId: number): void => {
    const vendor = cartItems.find((cart) => cart.vendor.id === vendorId);
    if (vendor && status === 'authenticated')
      removeItemMutate({ vendorId, productVariantId });
  };

  useEffect(() => {
    if (cartItems && vendorId && !firstLoad.current) {
      const updatedProducts =
        cartItems.find((cart) => cart.vendor.id === vendorId)?.products || [];

      if (
        updatedProducts.length > 0 &&
        updatedProducts.length === previousOrders.current.length &&
        previousOrders.current !== updatedProducts
      )
        previousOrders.current = updatedProducts;
    }
  }, [JSON.stringify(cartItems || {})]);

  useEffect(() => {
    if (status === 'authenticated' && !firstLoad.current) createBasket();
  }, [previousOrders.current]);

  useEffect(() => {
    if (firstLoad) firstLoad.current = false;
  }, []);

  return { setVendor, removeBasket, removeBasketItem, createBasket };
};
